import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { DataGrid } from "@mui/x-data-grid";
import ExcisePreFinanceGroupDetailDialog from "./ExcisePreFinanceGroupDetailDialog";
import {
  buildExciseCreateRequest,
  calculateSelectedExciseSummary,
  formatPreFinanceMoney,
  formatPreFinancePeriod,
} from "../utils/preFinancePreviewUtils";

import { getPreFinanceYearOptions } from "../utils/preFinanceYearOptions";
import {
  EXCISE_QUARTER_OPTIONS,
  getLatestElapsedQuarter,
  getQuarterBounds,
  isQuarterElapsed,
  usesExciseQuarterPicker,
} from "../utils/preFinancePeriodUtils";

const availableYears = getPreFinanceYearOptions();
const monthOptions = Array.from({ length: 12 }, (_, index) => ({
  value: index + 1,
  label: moment().month(index).format("MMMM"),
}));
const refreshPreviewErrorCodes = new Set([
  "StalePreview",
  "AlreadyPrefinanced",
  "DossierIdentityConflict",
  "PreFinanceGateBlocked",
  "InvalidOptionalClaim",
]);

const GRID_ROW_HEIGHT = 52;
const GRID_CHROME_HEIGHT = 143;
const PAGE_SIZE_STEP = 5;
const MIN_PAGE_SIZE = 5;
const MAX_PAGE_SIZE = 100;

const measureAvailableRowArea = (container) => {
  const containerHeight = container.getBoundingClientRect().height;
  if (containerHeight < 120) {
    return 0;
  }

  const gridRoot = container.querySelector(".MuiDataGrid-root");
  if (!gridRoot) {
    return Math.max(0, containerHeight - GRID_CHROME_HEIGHT);
  }

  const toolbar = gridRoot.querySelector(".MuiDataGrid-toolbarContainer");
  const columnHeaders = gridRoot.querySelector(".MuiDataGrid-columnHeaders");
  const footer = gridRoot.querySelector(".MuiDataGrid-footerContainer");
  const chromeHeight =
    (toolbar?.offsetHeight || 0) + (columnHeaders?.offsetHeight || 0) + (footer?.offsetHeight || 0);
  const gridHeight = gridRoot.getBoundingClientRect().height || containerHeight;

  return Math.max(0, gridHeight - chromeHeight);
};

const measureRowHeight = (gridRoot) => {
  if (!gridRoot) {
    return GRID_ROW_HEIGHT;
  }

  const renderedRow = gridRoot.querySelector(".MuiDataGrid-row");
  if (renderedRow?.offsetHeight > 0) {
    return renderedRow.offsetHeight;
  }

  const cssRowHeight = window.getComputedStyle(gridRoot).getPropertyValue("--DataGrid-rowHeight");
  const parsedRowHeight = Number.parseFloat(cssRowHeight);
  return Number.isFinite(parsedRowHeight) && parsedRowHeight > 0 ? parsedRowHeight : GRID_ROW_HEIGHT;
};

const calculatePageSizeForHeight = (rowAreaHeight, rowHeight = GRID_ROW_HEIGHT) => {
  if (rowAreaHeight <= 0 || rowHeight <= 0) {
    return MIN_PAGE_SIZE;
  }

  const rawCount = Math.floor(rowAreaHeight / rowHeight);
  const steppedCount = Math.floor(rawCount / PAGE_SIZE_STEP) * PAGE_SIZE_STEP;
  return Math.max(MIN_PAGE_SIZE, Math.min(MAX_PAGE_SIZE, steppedCount));
};

const buildRowsPerPageOptions = (optimalPageSize) => {
  const upperBound = Math.min(
    MAX_PAGE_SIZE,
    Math.max(optimalPageSize + PAGE_SIZE_STEP * 4, PAGE_SIZE_STEP * 6)
  );
  const options = new Set([optimalPageSize]);

  for (let size = PAGE_SIZE_STEP; size <= upperBound; size += PAGE_SIZE_STEP) {
    options.add(size);
  }

  return [...options].sort((a, b) => a - b);
};

const CalculationValue = ({ value, tooltip, fontWeight, summary = false }) => (
  <Box display="flex" alignItems="center" gap={0.5}>
    <Typography variant={summary ? "h6" : "body1"} color="inherit" fontWeight={fontWeight}>
      {value}
    </Typography>
    <Tooltip title={tooltip} arrow placement="top">
      <InfoOutlinedIcon
        aria-label="Calculation information"
        tabIndex={0}
        sx={{ fontSize: 15, cursor: "help" }}
      />
    </Tooltip>
  </Box>
);

const SummaryItem = ({ label, value, tooltip }) => (
  <Box>
    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
      {label}
    </Typography>
    {tooltip ? (
      <Box color="common.white">
        <CalculationValue value={value} tooltip={tooltip} summary />
      </Box>
    ) : (
      <Typography variant="h6" color="common.white">
        {value}
      </Typography>
    )}
  </Box>
);

const EmptyResults = ({ message }) => (
  <Box display="flex" alignItems="center" justifyContent="center" height="100%" p={3}>
    <Typography color="text.secondary">{message}</Typography>
  </Box>
);

const applyExcisePeriodDefaults = (agreements, selectedCountryId) => {
  if (!usesExciseQuarterPicker(agreements, selectedCountryId)) {
    return {
      year: new Date().getFullYear(),
      quarter: getLatestElapsedQuarter().quarter,
      monthFrom: 1,
      monthTo: 12,
    };
  }

  const elapsed = getLatestElapsedQuarter();
  const bounds = getQuarterBounds(elapsed.quarter);
  return {
    year: elapsed.year,
    quarter: elapsed.quarter,
    monthFrom: bounds.startMonth,
    monthTo: bounds.endMonth,
  };
};

const formatMissingQuarterMonths = (months = []) =>
  months.map((month) => moment().month(month - 1).format("MMMM")).join(", ");

const ExcisePreFinancePrepareDialog = ({ open, customerId, onClose, onCreated }) => {
  const [countries, setCountries] = useState([]);
  const [claimStates, setClaimStates] = useState([]);
  const [pricingAgreements, setPricingAgreements] = useState([]);
  const [countryId, setCountryId] = useState(0);
  const [year, setYear] = useState(new Date().getFullYear());
  const [monthFrom, setMonthFrom] = useState(1);
  const [monthTo, setMonthTo] = useState(12);
  const [quarter, setQuarter] = useState(getLatestElapsedQuarter().quarter);
  const [preview, setPreview] = useState(null);
  const [excludedSourceIds, setExcludedSourceIds] = useState([]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState([]);
  const [groupOptions, setGroupOptions] = useState({});
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState("");
  const [detailGroupKey, setDetailGroupKey] = useState(null);
  const [detailGroupSnapshot, setDetailGroupSnapshot] = useState(null);
  const [isPreviewStale, setIsPreviewStale] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [rowsPerPageOptions, setRowsPerPageOptions] = useState([5, 10, 15, 20]);
  const resultsGridRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    setCountryId(0);
    setYear(new Date().getFullYear());
    setMonthFrom(1);
    setMonthTo(12);
    setQuarter(getLatestElapsedQuarter().quarter);
    setPricingAgreements([]);
    setPreview(null);
    setExcludedSourceIds([]);
    setSelectedGroupKeys([]);
    setGroupOptions({});
    setError("");
    setDetailGroupKey(null);
    setDetailGroupSnapshot(null);
    setIsPreviewStale(false);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const controller = new AbortController();

    const loadLookups = async () => {
      try {
        const [countriesResponse, claimStatesResponse, pricingResponse] = await Promise.all([
          axios.get("/api/v1/exciseCountries", { signal: controller.signal }),
          axios.get("/api/v1/claimStates", { signal: controller.signal }),
          axios.get(`/api/v1/customerpricingagreements/entity/${customerId}`, {
            signal: controller.signal,
          }),
        ]);
        const nextAgreements = pricingResponse.data || [];
        setCountries(countriesResponse.data || []);
        setClaimStates(claimStatesResponse.data || []);
        setPricingAgreements(nextAgreements);
        const defaults = applyExcisePeriodDefaults(nextAgreements, 0);
        setYear(defaults.year);
        setQuarter(defaults.quarter);
        setMonthFrom(defaults.monthFrom);
        setMonthTo(defaults.monthTo);
      } catch (requestError) {
        if (!controller.signal.aborted) {
          console.error("Unable to load excise prefinance lookups:", requestError);
        }
      }
    };

    loadLookups();

    return () => controller.abort();
  }, [open, customerId]);

  const syncResultsGridPageSize = useCallback(() => {
    const container = resultsGridRef.current;
    if (!container || !open) {
      return;
    }

    const gridRoot = container.querySelector(".MuiDataGrid-root");
    const rowAreaHeight = measureAvailableRowArea(container);
    const rowHeight = measureRowHeight(gridRoot);
    const optimalPageSize = calculatePageSizeForHeight(rowAreaHeight, rowHeight);

    setPageSize((currentPageSize) => (currentPageSize === optimalPageSize ? currentPageSize : optimalPageSize));
    setRowsPerPageOptions(buildRowsPerPageOptions(optimalPageSize));
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let frameId = 0;

    const scheduleSync = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        syncResultsGridPageSize();
        frameId = requestAnimationFrame(syncResultsGridPageSize);
      });
    };

    scheduleSync();

    const container = resultsGridRef.current;
    const observer = container ? new ResizeObserver(scheduleSync) : null;
    if (container && observer) {
      observer.observe(container);
    }

    window.addEventListener("resize", scheduleSync);

    return () => {
      cancelAnimationFrame(frameId);
      observer?.disconnect();
      window.removeEventListener("resize", scheduleSync);
    };
  }, [open, isPreviewLoading, preview, syncResultsGridPageSize]);

  const handleRequestPreview = async (nextExcludedSourceIds = [], preserveSelection = false) => {
    setIsPreviewLoading(true);
    setError("");

    try {
      const response = await axios.post("/api/v1/preFinance/preview", {
        entityId: Number(customerId),
        preFinanceType: 1,
        countryId: countryId || null,
        year: Number(year),
        monthFrom: Number(monthFrom),
        monthTo: Number(monthTo),
        excludedSourceIds: nextExcludedSourceIds,
      });
      const nextPreview = response.data;
      const selectableKeys = nextPreview.groups
        .filter((group) => group.isSelectable)
        .map((group) => group.groupKey);

      setPreview(nextPreview);
      setIsPreviewStale(false);
      setDetailGroupSnapshot((currentGroup) => {
        if (!currentGroup) {
          return null;
        }

        const refreshedGroup = nextPreview.groups.find(
          (group) => group.groupKey === currentGroup.groupKey
        );
        return refreshedGroup || currentGroup;
      });
      setExcludedSourceIds(nextExcludedSourceIds);
      setSelectedGroupKeys((currentKeys) => {
        if (!preserveSelection) {
          return selectableKeys;
        }

        const currentKeySet = new Set(currentKeys);
        return selectableKeys.filter((key) => currentKeySet.has(key));
      });
      setGroupOptions((currentOptions) =>
        nextPreview.groups.reduce((options, group) => {
          const currentOption = currentOptions[group.groupKey];
          const suggestedClaimId = group.suggestedClaim?.claimId ?? null;

          options[group.groupKey] = {
            continueExistingPeriod:
              group.requiresExistingPeriodConfirmation &&
              Boolean(currentOption?.continueExistingPeriod),
            optionalClaimId:
              currentOption?.optionalClaimId !== undefined
                ? currentOption.optionalClaimId
                : suggestedClaimId,
          };
          return options;
        }, {})
      );
    } catch (requestError) {
      console.error("Unable to preview excise prefinance:", requestError);
      setError(requestError.response?.data?.message || "The excise prefinance preview could not be loaded.");
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleSelectionChange = () => {
    setIsPreviewStale(Boolean(preview));
    setError("");
    setDetailGroupKey(null);
    setDetailGroupSnapshot(null);
  };

  const handleYearChange = (event) => {
    setYear(event.target.value);
    handleSelectionChange();
  };

  const handleCountryChange = (event) => {
    const nextCountryId = Number(event.target.value);
    const defaults = applyExcisePeriodDefaults(pricingAgreements, nextCountryId);
    setCountryId(nextCountryId);
    if (usesExciseQuarterPicker(pricingAgreements, nextCountryId)) {
      const bounds = getQuarterBounds(quarter);
      setMonthFrom(bounds.startMonth);
      setMonthTo(bounds.endMonth);
    } else {
      setYear(defaults.year);
      setMonthFrom(defaults.monthFrom);
      setMonthTo(defaults.monthTo);
    }
    handleSelectionChange();
  };

  const handleQuarterChange = (event) => {
    const nextQuarter = Number(event.target.value);
    const bounds = getQuarterBounds(nextQuarter);
    setQuarter(nextQuarter);
    setMonthFrom(bounds.startMonth);
    setMonthTo(bounds.endMonth);
    handleSelectionChange();
  };

  const handleMonthFromChange = (event) => {
    const nextMonth = Number(event.target.value);
    setMonthFrom(nextMonth);
    if (nextMonth > monthTo) {
      setMonthTo(nextMonth);
    }
    handleSelectionChange();
  };

  const handleMonthToChange = (event) => {
    const nextMonth = Number(event.target.value);
    setMonthTo(nextMonth);
    if (nextMonth < monthFrom) {
      setMonthFrom(nextMonth);
    }
    handleSelectionChange();
  };

  const handleGroupSelection = (groupKey) => {
    setSelectedGroupKeys((currentKeys) =>
      currentKeys.includes(groupKey)
        ? currentKeys.filter((key) => key !== groupKey)
        : [...currentKeys, groupKey]
    );
  };

  const handleGroupOptionChange = (groupKey, field, value) => {
    setGroupOptions((currentOptions) => ({
      ...currentOptions,
      [groupKey]: {
        ...currentOptions[groupKey],
        [field]: value,
      },
    }));
  };

  const usesQuarterPicker = usesExciseQuarterPicker(pricingAgreements, countryId);
  const periodNotElapsed = usesQuarterPicker && !isQuarterElapsed(year, quarter);
  const selectedSummary = useMemo(
    () => calculateSelectedExciseSummary(preview?.groups || [], selectedGroupKeys),
    [preview, selectedGroupKeys]
  );

  const selectedGroups = (preview?.groups || []).filter((group) =>
    selectedGroupKeys.includes(group.groupKey)
  );
  const unconfirmedExistingPeriodGroups = selectedGroups.filter(
    (group) =>
      group.requiresExistingPeriodConfirmation &&
      !groupOptions[group.groupKey]?.continueExistingPeriod
  );
  const hasUnconfirmedExistingPeriod = unconfirmedExistingPeriodGroups.length > 0;
  const incompleteQuarterGroups = (preview?.groups || []).filter(
    (group) => group.hasIncompleteQuarter
  );
  const unelapsedPeriodGroups = (preview?.groups || []).filter(
    (group) => group.exclusionReason === "PeriodNotElapsed"
  );
  const canRequestPreview =
    !isPreviewLoading && !isCreating && !periodNotElapsed;
  const canCreate =
    preview?.canPreview &&
    selectedGroups.length > 0 &&
    !hasUnconfirmedExistingPeriod &&
    !periodNotElapsed &&
    !isPreviewStale &&
    !isPreviewLoading &&
    !isCreating;

  const handleCreate = async () => {
    if (!canCreate) {
      return;
    }

    setIsCreating(true);
    setError("");

    try {
      const request = buildExciseCreateRequest({
        entityId: customerId,
        countryId,
        year,
        monthFrom,
        monthTo,
        excludedSourceIds,
        groups: preview.groups,
        selectedGroupKeys,
        groupOptions,
      });
      const response = await axios.post("/api/v1/preFinance/create", request);
      onCreated(response.data);
      onClose();
    } catch (requestError) {
      console.error("Unable to create excise prefinance:", requestError);
      const errorCode = requestError.response?.data?.error;
      const errorMessage =
        requestError.response?.data?.message ||
        "The excise prefinance dossiers could not be created.";

      if (refreshPreviewErrorCodes.has(errorCode)) {
        await handleRequestPreview(excludedSourceIds, true);
        setError(`${errorMessage} The preview has been refreshed.`);
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const getCountryLabel = (nextCountryId) => {
    const country = countries.find((item) => item.countryId === nextCountryId);
    return country
      ? `${country.countryCode} - ${country.countryName}`
      : `Country ${nextCountryId}`;
  };

  const resultRows = preview?.canPreview ? preview.groups : [];
  const detailGroup = detailGroupKey ? detailGroupSnapshot : null;
  const handleOpenGroupDetails = (group) => {
    setDetailGroupKey(group.groupKey);
    setDetailGroupSnapshot(group);
  };

  const resultColumns = [
    {
      field: "selected",
      headerName: "Exclude",
      width: 85,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Checkbox
          checked={!selectedGroupKeys.includes(row.groupKey)}
          disabled={!row.isSelectable}
          onChange={() => handleGroupSelection(row.groupKey)}
          inputProps={{
            "aria-label": `Exclude ${row.previewPreFinanceCode || row.groupKey}`,
          }}
        />
      ),
    },
    {
      field: "countryId",
      headerName: "Country",
      minWidth: 145,
      flex: 0.8,
      valueGetter: ({ row }) => getCountryLabel(row.countryId),
    },
    {
      field: "period",
      headerName: "Period",
      width: 150,
      valueGetter: ({ row }) => formatPreFinancePeriod(row),
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <span>{formatPreFinancePeriod(row)}</span>
          {row.hasIncompleteQuarter ? (
            <Tooltip
              title={`Missing months: ${formatMissingQuarterMonths(row.missingQuarterMonths)}`}
            >
              <WarningAmberIcon
                aria-label="Incomplete quarter"
                tabIndex={0}
                color="warning"
                sx={{ fontSize: 16 }}
              />
            </Tooltip>
          ) : null}
        </Box>
      ),
    },
    {
      field: "previewPreFinanceCode",
      headerName: "Prefinance Code",
      minWidth: 205,
      flex: 1,
    },
    {
      field: "totalLiters",
      headerName: "Liters",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueFormatter: ({ value }) => formatPreFinanceMoney(value),
    },
    {
      field: "refundAmount",
      headerName: "Amount EUR",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: ({ value }) => formatPreFinanceMoney(value),
    },
    {
      field: "declarationAmount",
      headerName: "Amount local",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: ({ value }) => formatPreFinanceMoney(value),
    },
    {
      field: "payoutAmount",
      headerName: "Total Payable Amount",
      width: 165,
      align: "right",
      headerAlign: "right",
      valueFormatter: ({ value }) => formatPreFinanceMoney(value),
    },
    {
      field: "confirmation",
      headerName: "Confirmation",
      width: 125,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        if (!row.requiresExistingPeriodConfirmation) {
          return "—";
        }

        if (groupOptions[row.groupKey]?.continueExistingPeriod) {
          return <Chip label="Confirmed" color="success" size="small" />;
        }

        return (
          <Button
            size="small"
            color="warning"
            variant="outlined"
            startIcon={<WarningAmberIcon fontSize="small" />}
            onClick={() => handleOpenGroupDetails(row)}
          >
            Confirm
          </Button>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 85,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Tooltip title="Open excise declarations">
          <IconButton
            aria-label={`Open declarations for ${row.previewPreFinanceCode || row.groupKey}`}
            onClick={() => handleOpenGroupDetails(row)}
            size="small"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={isCreating ? undefined : onClose}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            height: "85vh",
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">Create pre-finance Excise</Typography>
              <Typography variant="body2" color="text.secondary">
                {usesQuarterPicker
                  ? "Preview quarterly excise declarations and create server-validated dossiers"
                  : "Preview monthly excise declarations and create server-validated dossiers"}
              </Typography>
            </Box>
            <IconButton
              aria-label="Close excise prefinance preparation"
              onClick={onClose}
              disabled={isCreating}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            flex: "1 1 0",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          <Box sx={{ flexShrink: 0 }}>
            <Grid container xs={12} sx={{ mb: 3 }}>
              <Grid item container xs={12} md={8} spacing={5} sx={{ pr: { md: 2 } }}>
                <Grid item xs={12} sm={5}>
                  <TextField
                    select
                    fullWidth
                    label="Country"
                    value={countryId}
                    onChange={handleCountryChange}
                    disabled={isPreviewLoading || isCreating}
                  >
                    <MenuItem value={0}>All countries</MenuItem>
                    {countries.map((country) => (
                      <MenuItem key={country.countryId} value={country.countryId}>
                        {country.countryCode} - {country.countryName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    select
                    fullWidth
                    label="Year"
                    value={year}
                    onChange={handleYearChange}
                    disabled={isPreviewLoading || isCreating}
                  >
                    {availableYears.map((availableYear) => (
                      <MenuItem key={availableYear} value={availableYear}>
                        {availableYear}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={5}>
                  {usesQuarterPicker ? (
                    <TextField
                      select
                      fullWidth
                      label="Period"
                      value={quarter}
                      onChange={handleQuarterChange}
                      disabled={isPreviewLoading || isCreating}
                    >
                      {EXCISE_QUARTER_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField
                      select
                      fullWidth
                      label="Start month"
                      value={monthFrom}
                      onChange={handleMonthFromChange}
                      disabled={isPreviewLoading || isCreating}
                    >
                      {monthOptions.map((month) => (
                        <MenuItem key={month.value} value={month.value}>
                          {month.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Grid>
                {usesQuarterPicker ? null : (
                  <Grid item xs={12} sm={5}>
                    <TextField
                      select
                      fullWidth
                      label="End month"
                      value={monthTo}
                      onChange={handleMonthToChange}
                      disabled={isPreviewLoading || isCreating}
                    >
                      {monthOptions.map((month) => (
                        <MenuItem key={month.value} value={month.value}>
                          {month.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    sx={{ mt: 6 }}
                    onClick={() => handleRequestPreview([], false)}
                    disabled={!canRequestPreview}
                    aria-label="Preview pre-finance Excise"
                  >
                    {isPreviewLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Preview pre-finance Excise"
                    )}
                  </Button>
                </Grid>
              </Grid>

              <Grid item xs={12} md={4} sx={{ bgcolor: "#376fd0" }}>
                <Box sx={{ p: 5, pt: 3 }}>
                  <Typography variant="h6" color="common.white" gutterBottom>
                    Selected summary
                  </Typography>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                    <SummaryItem label="Dossiers" value={selectedSummary.groupCount} />
                    <SummaryItem label="Declarations" value={selectedSummary.sourceCount} />
                    <SummaryItem label="Liters" value={formatPreFinanceMoney(selectedSummary.totalLiters)} />
                    <SummaryItem
                      label="Amount EUR"
                      value={formatPreFinanceMoney(selectedSummary.totalRefundAmount)}
                    />
                    <SummaryItem
                      label="Amount local"
                      value={formatPreFinanceMoney(selectedSummary.totalDeclarationAmount)}
                    />
                    <SummaryItem
                      label="Costs"
                      value={formatPreFinanceMoney(selectedSummary.totalCosts)}
                      tooltip={
                        <Box>
                          <Typography variant="body2">
                            VF costs ={" "}
                            {selectedSummary.vfPercentages.length === 1
                              ? `${formatPreFinanceMoney(
                                  selectedSummary.totalRefundAmount
                                )} × ${formatPreFinanceMoney(
                                  selectedSummary.vfPercentages[0]
                                )}%`
                              : `selected amounts × ${selectedSummary.vfPercentages
                                  .map(
                                    (percentage) => `${formatPreFinanceMoney(percentage)}%`
                                  )
                                  .join(", ")}`}{" "}
                            = {formatPreFinanceMoney(selectedSummary.totalRefundFee)}
                          </Typography>
                          {selectedSummary.totalVatOnDeclarationFee > 0 ? (
                            <Typography variant="body2">
                              NL VAT costs ={" "}
                              {formatPreFinanceMoney(selectedSummary.totalRefundFee)} × 21% ={" "}
                              {formatPreFinanceMoney(selectedSummary.totalVatOnDeclarationFee)}
                            </Typography>
                          ) : (
                            <Typography variant="body2">
                              NL VAT costs = not applicable = 0,00
                            </Typography>
                          )}
                          <Typography variant="body2" fontWeight={600}>
                            Total costs ={" "}
                            {formatPreFinanceMoney(selectedSummary.totalRefundFee)} +{" "}
                            {formatPreFinanceMoney(selectedSummary.totalVatOnDeclarationFee)} ={" "}
                            {formatPreFinanceMoney(selectedSummary.totalCosts)}
                          </Typography>
                        </Box>
                      }
                    />
                    <SummaryItem
                      label="Payable"
                      value={formatPreFinanceMoney(selectedSummary.totalPayoutAmount)}
                    />
                    <SummaryItem label="Missing PDFs" value={selectedSummary.missingFileCount} />
                  </Box>
                  {preview?.gate?.missingDocumentTypeIds?.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Missing document types: {preview.gate.missingDocumentTypeIds.join(", ")}
                    </Alert>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ flexShrink: 0 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {periodNotElapsed && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                The selected period has not ended yet. Prefinance becomes available after the
                quarter has elapsed.
              </Alert>
            )}

            {preview && isPreviewStale && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                The filter options have changed. Preview again to refresh the results.
              </Alert>
            )}

            {preview && !preview.canPreview && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Prefinance is not available for this entity:
                </Typography>
                {preview.gate.issues.map((issue) => (
                  <Typography key={`${issue.code}-${issue.documentTypeId || "general"}`} variant="body2">
                    • {issue.message}
                  </Typography>
                ))}
              </Alert>
            )}

            {hasUnconfirmedExistingPeriod && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  A pre-finance dossier already exists for this exact period. Confirm that you want
                  to create an additional version for:{" "}
                  <strong>
                    {unconfirmedExistingPeriodGroups
                      .map(
                        (group) =>
                          `${group.previewPreFinanceCode || group.groupKey} · ${getCountryLabel(
                            group.countryId
                          )} · ${formatPreFinancePeriod(group)}`
                      )
                      .join("; ")}
                  </strong>
                  . Use Confirm in the corresponding result row.
                </Typography>
              </Alert>
            )}

            {incompleteQuarterGroups.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  One or more months in this quarter have no declaration yet:{" "}
                  <strong>
                    {incompleteQuarterGroups
                      .map(
                        (group) =>
                          `${getCountryLabel(group.countryId)} · ${formatPreFinancePeriod(
                            group
                          )} (missing ${formatMissingQuarterMonths(group.missingQuarterMonths)})`
                      )
                      .join("; ")}
                  </strong>
                  . You can still create the dossier.
                </Typography>
              </Alert>
            )}

            {unelapsedPeriodGroups.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                The selected period has not ended yet. Those groups stay visible but cannot be
                created until the quarter has elapsed.
              </Alert>
            )}
          </Box>

          <Box
            ref={resultsGridRef}
            sx={{
              flex: 1,
              minHeight: 240,
              height: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ flexShrink: 0 }}>
              Results
            </Typography>
            <Box sx={{ flex: 1, minHeight: 180, height: 0, minWidth: 0 }}>
              <DataGrid
                getRowId={(row) => row.groupKey}
                rows={resultRows}
                columns={resultColumns}
                disableSelectionOnClick
                density="compact"
                pagination
                paginationMode="client"
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={rowsPerPageOptions}
                loading={isPreviewLoading}
                components={{
                  NoRowsOverlay: () => (
                    <EmptyResults
                      message={
                        preview
                          ? "No eligible excise declarations were found for this period."
                          : "Select the filter options and start the preview to show results."
                      }
                    />
                  ),
                }}
                sx={{
                  height: "100%",
                  minHeight: 180,
                  borderRadius: 1,
                  backgroundColor: "background.paper",
                  boxShadow: 2,
                }}
              />
            </Box>

            {preview?.excludedSources?.length > 0 && (
              <Accordion
                disableGutters
                sx={{
                  flexShrink: 0,
                  mt: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    Excluded declarations ({preview.excludedSources.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    px: 1,
                    py: 0,
                    maxHeight: "min(28vh, 320px)",
                    overflowY: "auto",
                  }}
                >
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small" stickyHeader aria-label="Excluded excise declarations">
                      <TableHead>
                        <TableRow>
                          <TableCell>Claim code</TableCell>
                          <TableCell>Country</TableCell>
                          <TableCell>Reason</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {preview.excludedSources.map((source) => (
                          <TableRow key={source.sourceId}>
                            <TableCell>{source.sourceCode}</TableCell>
                            <TableCell>{getCountryLabel(source.countryId)}</TableCell>
                            <TableCell>{source.exclusionReason}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
          <Button type="button" variant="contained" color="primary" onClick={onClose} disabled={isCreating}>
            Close
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={!canCreate}>
            {isCreating ? <CircularProgress size={24} color="inherit" /> : "Create pre-finance Excise"}
          </Button>
        </DialogActions>
      </Dialog>

      <ExcisePreFinanceGroupDetailDialog
        open={Boolean(detailGroup)}
        group={detailGroup}
        countryLabel={detailGroup ? getCountryLabel(detailGroup.countryId) : ""}
        claimStates={claimStates}
        option={detailGroup ? groupOptions[detailGroup.groupKey] : null}
        isBusy={isPreviewLoading || isCreating}
        onClose={() => {
          setDetailGroupKey(null);
          setDetailGroupSnapshot(null);
        }}
        onGroupOptionChange={handleGroupOptionChange}
      />
    </React.Fragment>
  );
};

export default ExcisePreFinancePrepareDialog;
